/**
 * Subscription Context
 * Provides subscription status and management throughout the app
 * Uses Paymob API endpoints
 */
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { UserContext } from './UserContext';
import { getSubscriptionStatus } from '../api/subscriptionApi';

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const { Authorization } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('none');
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [trialStart, setTrialStart] = useState(null);
  const [trialEnd, setTrialEnd] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [currentPlanName, setCurrentPlanName] = useState('Free');
  const [animalLimit, setAnimalLimit] = useState(null);
  const [subscriptionCurrentPeriodEnd, setSubscriptionCurrentPeriodEnd] = useState(null);
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState(null);
  const [registerationType, setRegisterationType] = useState(null);

  /**
   * Fetch subscription status from the backend
   */
  const loadSubscriptionStatus = async () => {
    if (!Authorization) {
      // No auth token, reset to default state
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const statusData = await getSubscriptionStatus();
      
      // Update state with fetched data from Paymob API response
      setSubscriptionStatus(statusData.subscriptionStatus || 'none');
      setIsTrialActive(statusData.isTrialActive || false);
      setIsTrialExpired(statusData.isTrialExpired || false);
      setTrialStart(statusData.trialStart || null);
      setTrialEnd(statusData.trialEnd || null);
      setPlanId(statusData.planId || null);
      setCurrentPlanName(statusData.isPaidSubscription?.name || 'Free');
      setAnimalLimit(statusData.animalLimit || null);
      setSubscriptionCurrentPeriodEnd(statusData.subscriptionCurrentPeriodEnd || null);
      setIsSubscriptionExpired(statusData.isSubscriptionExpired || false);
      setDaysUntilExpiry(statusData.daysUntilExpiry || null);
      setRegisterationType(statusData.registerationType || null);
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      setError(err.message || 'Failed to load subscription status');
      // Set defaults on error
      setSubscriptionStatus('none');
      setIsTrialActive(false);
      setIsTrialExpired(false);
      setIsSubscriptionExpired(false);
      setCurrentPlanName('Free');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh subscription status (useful after checkout completion)
   */
  const refreshSubscription = () => {
    loadSubscriptionStatus();
  };

  // Load subscription status on mount and when auth changes
  useEffect(() => {
    loadSubscriptionStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Authorization]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    loading,
    error,
    subscriptionStatus,
    isTrialActive,
    isTrialExpired,
    trialStart,
    trialEnd,
    planId,
    currentPlanName,
    animalLimit,
    subscriptionCurrentPeriodEnd,
    isSubscriptionExpired,
    daysUntilExpiry,
    registerationType,
    refreshSubscription,
    // Legacy support - keep planName for backward compatibility
    planName: currentPlanName,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    loading,
    error,
    subscriptionStatus,
    isTrialActive,
    isTrialExpired,
    trialStart,
    trialEnd,
    planId,
    currentPlanName,
    animalLimit,
    subscriptionCurrentPeriodEnd,
    isSubscriptionExpired,
    daysUntilExpiry,
    registerationType,
  ]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to access subscription context
 * @returns {Object} Subscription state and methods
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

